import * as Phaser from 'phaser'
import {
  ANIMATION_IDENTIFIERS,
  DrawAnimationConfig,
  DrawAnimationResult,
  GAME_MODE,
  SCENE_KEYS,
} from '@Animation/types'
import { store } from '@AppState/redux-store'
import { AnimationActions, ANIMATION_PLAY_STATE, ANIMATION_CANVAS_STATE } from '@AppState/animation'
import { KenoAnimationScene } from './scenes/keno-animation'
import { ANIMATION_CONFIG_MAP } from './animation-map'
import { LoadingScene } from './scenes/loading'

import Analytics, { AnalyticEvents } from '@Util/analytics'
import { AudioKeyTemplate } from './constants'

let animation: Phaser.Game | null = null
let defaultDiv: HTMLDivElement
let currentParent: HTMLDivElement
let sounds: AudioKeyTemplate | undefined = undefined


const setSounds = (newSounds: AudioKeyTemplate | undefined) => {
  sounds = newSounds
}

const getSounds = () => {
  return sounds
}

const getAnimation = () => {
  if (animation === undefined) {
    throw new Error(`Game has not been initialized yet`)
  }
  return animation
}

const setParentDiv = (parentRef: HTMLDivElement = defaultDiv) => {
  if (currentParent) {
    resizeObserver.unobserve(currentParent)
  }
  if (!animation) return

  currentParent = parentRef
  parentRef.appendChild(animation.canvas)
  animation.scale.parent = parentRef

  if (parentRef === defaultDiv) return
  resizeObserver.observe(currentParent)

  updateSize(parentRef.clientWidth, parentRef.clientHeight)

  animation.scene.stop(SCENE_KEYS.DRAW_ANIMATION)
}

const updateSize = (width: number, height: number) => {
  if (!animation) return

  animation.scale.setParentSize(width, height)
}

const updateGameSpeed = (gameSpeed: number) => {
  const currentScene = getDrawAnimationScene()
  if (!currentScene) return

  currentScene.setGameSpeed(gameSpeed)
}

const resizeObserver = new ResizeObserver((entries) => {
  if (!animation) return
  for (const entry of entries) {
    updateSize(entry.contentRect.width, entry.contentRect.height)
  }
})

const create = (parentRef: HTMLDivElement): void => {
  const state = store.getState()

  if (state.animation.canvasState === ANIMATION_CANVAS_STATE.DESTROYING) {
    if (!animation) return

    animation.events.on('destroy', () => {
      create(parentRef)
    })

    return
  }

  if (state.animation.canvasState !== ANIMATION_CANVAS_STATE.INIT) {
    return
  }

  defaultDiv = parentRef

  const config: Phaser.Types.Core.GameConfig = {
    antialias: true,
    audio: {
      disableWebAudio: false,
    },
    backgroundColor: 'rgba(0, 0, 0, 0)',
    callbacks: {
      preBoot: () =>
        store.dispatch(AnimationActions.setCanvasState(ANIMATION_CANVAS_STATE.GAME_LOADING)),
      postBoot: (game: Phaser.Game) => {
        animation = game
      },
    },
    height: 500,
    input: {
      mouse: { preventDefaultWheel: false },
      touch: false,
    },
    parent: parentRef,
    render: {
      transparent: true,
    },
    roundPixels: true,
    scale: {
      mode: Phaser.Scale.ScaleModes.FIT,
    },
    scene: [LoadingScene, KenoAnimationScene],
    type: Phaser.AUTO,
    width: 500,
  }

  animation = new Phaser.Game(config)

  animation.events.on('destroy', () => {
    animation = null
    store.dispatch(AnimationActions.setCanvasState(ANIMATION_CANVAS_STATE.INIT))
  })

  animation.events.on('ready', () => {
    store.dispatch(AnimationActions.setCanvasState(ANIMATION_CANVAS_STATE.READY))
  })
}

const destroy = () => {
  const animationState = store.getState().animation.canvasState

  if (
    animationState === ANIMATION_CANVAS_STATE.INIT ||
    animationState === ANIMATION_CANVAS_STATE.DESTROYING
  ) {
    return
  }

  store.dispatch(AnimationActions.setCanvasState(ANIMATION_CANVAS_STATE.DESTROYING))

  animation?.destroy(true)
}

const getDrawAnimationScene = () => {
  if (!animation) return null
  const animationScene = animation.scene.getScene(
    SCENE_KEYS.DRAW_ANIMATION,
  ) as KenoAnimationScene | null
  const isActive = animation.scene.isActive(SCENE_KEYS.DRAW_ANIMATION)
  const isPaused = animation.scene.isPaused(SCENE_KEYS.DRAW_ANIMATION)
  return isPaused || isActive ? animationScene : null
}

const playDrawResult = async (
  result: DrawAnimationResult,
  animationIdentifier: ANIMATION_IDENTIFIERS,
  analyticsMetadata: { track: boolean; mode: 'view' | 'ticket' | null } & Record<
    string,
    unknown
  > = {
    track: false,
    mode: null,
  },
) => {
  const settings = store.getState().appSettings.appSettings
  const startPaused = store.getState().animation.isPaused

  if (analyticsMetadata.track) {
    const { track: _ignore, ...metadata } = analyticsMetadata
    Analytics.trackDrawing(AnalyticEvents.VIEWING_DRAWING, result.drawNumber, {
      activeAnimation: animationIdentifier,
      ...metadata,
    })
  }

  const config: DrawAnimationConfig = {
    mechanicConfig: {
      ...ANIMATION_CONFIG_MAP[animationIdentifier],
    },
    results: {
      ...result,
    },
    settings: {
      gameSpeed: settings.gameSpeed,
      gameMode:
        result.ticketInfo.baseSelectedNumbers.length > 0 ? GAME_MODE.TICKET : GAME_MODE.VIEW,
      startPaused: startPaused,
    },
  }

  animation?.scene.start(SCENE_KEYS.DRAW_ANIMATION, config)
}

const restart = () => {
  const scene = getDrawAnimationScene()
  if (!scene) return
  scene.playNewDraw(scene.gameConfig)
}

const stopDrawAnimation = () => {
  getDrawAnimationScene()?.stopAnimation()
}

const skipToEnd = () => {
  const drawScene = getDrawAnimationScene()
  drawScene?.skipAnimation()
}

const setPause = async (paused: boolean) => {
  if (!animation) return

  const drawScene = animation.scene.getScene(SCENE_KEYS.DRAW_ANIMATION) as KenoAnimationScene | null
  const scenePaused = animation.scene.isPaused(SCENE_KEYS.DRAW_ANIMATION)
  const sceneActive = animation.scene.isActive(SCENE_KEYS.DRAW_ANIMATION)

  if (!drawScene) {
    console.log(`Scene cannot be paused because it does not exist`)
    return
  }

  store.dispatch(
    AnimationActions.setPlayState(
      paused ? ANIMATION_PLAY_STATE.PAUSED : ANIMATION_PLAY_STATE.PLAYING,
    ),
  )

  if (drawScene.gameConfig) {
    drawScene.gameConfig.settings.startPaused = paused
  }

  if (!scenePaused && !sceneActive) return

  if (paused) {
    drawScene.scene.pause()
  } else {
    drawScene.scene.resume()
  }
}

export const GameController = {
  create,
  destroy,
  getAnimation,
  playDrawResult,
  restart,
  setParentDiv,
  setPause,
  skipToEnd,
  stopDrawAnimation,
  updateGameSpeed,
  getDrawAnimationScene,
  setSounds,
  getSounds
}
